import { AxiosRequestHeaders } from 'axios';
import { capability } from '../rpc-client/config/capabilities';
import RpcClientFactory from '../rpc-client/factory/rpc-client-factory';
import { DtoTypes } from '../';
import { ReplacementCarDTO } from '../types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa/motor/replacementcar.dto';
import { ConflictingCoverableDTO } from '../types/dto/pc/top/edge/capabilities/onlinesales/dto/quote';
import { CustomerStopDTO } from '../types/dto/pc/top/edge/capabilities/onlinesales/dto';
import { SeniorityReasons_Top } from '../types/dto/pc/typekey';

export async function getJobDTO(
    jobNumber: string,
    headers: AxiosRequestHeaders = {}
): Promise<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.JobDTO> {
    return await RpcClientFactory.getInstance().rpcCall<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.JobDTO>(
        capability.PC_ONLINESALES,
        'getJobDTO',
        [jobNumber],
        headers
    );
}

export async function getSessionId(
    jobNumber: string,
    headers: AxiosRequestHeaders = {}
): Promise<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.SessionIdDTO> {
    return await RpcClientFactory.getInstance().rpcCall<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.SessionIdDTO>(
        capability.PC_ONLINESALES,
        'getSessionId',
        [jobNumber],
        headers
    );
}

export async function getAccountHolderDetails(
    jobNumber: string,
    sessionId: string,
    headers: AxiosRequestHeaders = {}
): Promise<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.account.AccountDTO> {
    return await RpcClientFactory.getInstance().rpcCall<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.account.AccountDTO>(
        capability.PC_ONLINESALES,
        'getAccountHolderDetails',
        [jobNumber, sessionId],
        headers
    );
}

export async function getPaymentInformation(
    jobNumber: string,
    sessionId: string,
    headers: AxiosRequestHeaders = {}
): Promise<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.payment.PaymentInformationDTO> {
    return await RpcClientFactory.getInstance().rpcCall<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.payment.PaymentInformationDTO>(
        capability.PC_ONLINESALES,
        'getPaymentInformation',
        [jobNumber, sessionId],
        headers
    );
}

export async function getVehicleSeniorityReason(
    jobNumber: string,
    sessionId: string,
    fixedId: string,
    headers: AxiosRequestHeaders = {}
): Promise<SeniorityReasons_Top> {
    return await RpcClientFactory.getInstance().rpcCall<SeniorityReasons_Top>(
        capability.PC_ONLINESALES,
        'getVehicleSeniorityReason',
        [jobNumber, sessionId, fixedId],
        headers
    );
}

export async function validateBankAccountData(
    bankRef: string,
    accountNo: string,
    headers: AxiosRequestHeaders = {}
): Promise<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.payment.BankAccountStatusDTO> {
    return await RpcClientFactory.getInstance().rpcCall<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.payment.BankAccountStatusDTO>(
        capability.PC_ONLINESALES,
        'validateBankAccountData',
        [bankRef, accountNo],
        headers
    );
}

export async function updateMissingInformation(
    jobNumber: string,
    sessionId: string,
    missingInformation: DtoTypes.PC.top.edge.capabilities.onlinesales.dto.missingInformation.MissingInformationDTO,
    headers: AxiosRequestHeaders = {}
): Promise<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.missingInformation.MissingInformationDTO> {
    return await RpcClientFactory.getInstance().rpcCall<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.missingInformation.MissingInformationDTO>(
        capability.PC_ONLINESALES,
        'updateMissingInformation',
        [jobNumber, sessionId, missingInformation],
        headers
    );
}

export async function updatePaymentInformation(
    paymentData: DtoTypes.PC.top.edge.capabilities.onlinesales.dto.payment.UpdatePaymentDTO,
    sessionId: string,
    headers: AxiosRequestHeaders = {}
): Promise<void> {
    return await RpcClientFactory.getInstance().rpcCall<void>(
        capability.PC_ONLINESALES,
        'updatePaymentInformation',
        [paymentData, sessionId],
        headers
    );
}

export async function generateAndGetEdiCases(
    jobNumber: string,
    sessionId: string,
    headers: AxiosRequestHeaders = {}
): Promise<DtoTypes.PC.top.edge.capabilities.policyjob.edi.dto.EdiCancellationDTO> {
    return await RpcClientFactory.getInstance().rpcCall(
        capability.PC_ONLINESALES,
        'generateAndGetEdiCases',
        [jobNumber, sessionId],
        headers
    );
}

export async function issue(
    submissionId: string,
    sessionId: string,
    headers: AxiosRequestHeaders = {}
): Promise<boolean> {
    return await RpcClientFactory.getInstance().rpcCall<boolean>(
        capability.PC_ONLINESALES,
        'issue',
        [submissionId, sessionId],
        headers
    );
}

export async function quoteJob(
    submissionId: string,
    sessionId: string,
    headers: AxiosRequestHeaders = {}
): Promise<CustomerStopDTO> {
    return await RpcClientFactory.getInstance().rpcCall<CustomerStopDTO>(
        capability.PC_ONLINESALES,
        'quoteJob',
        [submissionId, sessionId],
        headers
    );
}

export async function saveAllEdi(
    jobNumber: string,
    sessionId: string,
    headers: AxiosRequestHeaders = {},
    ediCases: DtoTypes.PC.top.edge.capabilities.policyjob.edi.dto.ExternaleCaseDTO[]
): Promise<void> {
    return await RpcClientFactory.getInstance().rpcCall(
        capability.PC_ONLINESALES,
        'saveAllEdi',
        [jobNumber, ediCases, sessionId],
        headers
    );
}

export async function saveChaConsent(
    jobNumber: string,
    updateChaConsentDto: DtoTypes.PC.top.edge.capabilities.onlinesales.dto.common.UpdateChaConsentDTO,
    sessionId: string,
    headers: AxiosRequestHeaders = {}
): Promise<void> {
    return await RpcClientFactory.getInstance().rpcCall(
        capability.PC_ONLINESALES,
        'saveChaConsent',
        [jobNumber, updateChaConsentDto, sessionId],
        headers
    );
}

export async function updateSalesforceAccountInformation(
    jobId: string,
    sessionId: string,
    contactInfo: DtoTypes.PC.top.edge.capabilities.onlinesales.dto.account.UpdateContactInfoDTO,
    headers: AxiosRequestHeaders = {}
): Promise<void> {
    return await RpcClientFactory.getInstance().rpcCall<void>(
        capability.PC_ONLINESALES,
        'updateSalesforceAccountInformation',
        [jobId, sessionId, contactInfo],
        headers
    );
}

export async function getAllInsuranceCompanies(
    headers: AxiosRequestHeaders = {}
): Promise<DtoTypes.PC.top.edge.capabilities.insurancecompany.dto.InsuranceCompanyDTO[]> {
    return await RpcClientFactory.getInstance().rpcCall(
        capability.PC_INSURANCE_COMPANY_LOOKUP,
        'getAllInsuranceCompanies',
        [],
        headers
    );
}

export async function getAdditionalNameInsured(
    jobId: string,
    sessionId: string,
    headers: AxiosRequestHeaders = {}
): Promise<DtoTypes.PC.top.edge.capabilities.policycommon.additionalnameinsured.AdditionalNameInsuredDTO[]> {
    return await RpcClientFactory.getInstance().rpcCall(
        capability.PC_ONLINESALES,
        'getAdditionalNameInsured',
        [jobId, sessionId],
        headers
    );
}

export async function getCustomerBenefits(
    jobId: string,
    sessionId: string,
    headers: AxiosRequestHeaders = {}
): Promise<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.CustomerBenefitsDTO> {
    return await RpcClientFactory.getInstance().rpcCall<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.CustomerBenefitsDTO>(
        capability.PC_ONLINESALES,
        'getCustomerBenefits',
        [jobId, sessionId],
        headers
    );
}

export async function getVIDCustomerStop(
    jobId: string,
    accountNumber: string,
    sessionId: string,
    headers: AxiosRequestHeaders = {}
): Promise<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.CustomerStopDTO> {
    return await RpcClientFactory.getInstance().rpcCall<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.CustomerStopDTO>(
        capability.PC_ONLINESALES,
        'getVIDCustomerStop',
        [jobId, accountNumber, sessionId],
        headers
    );
}

export async function checkCoverableForDoubleInsurance(
    jobId: string,
    linePrefix: string,
    coverableFixedId: string,
    accountNumber: string,
    sessionId: string,
    headers: AxiosRequestHeaders = {}
): Promise<ConflictingCoverableDTO> {
    return await RpcClientFactory.getInstance().rpcCall<ConflictingCoverableDTO>(
        capability.PC_ONLINESALES,
        'checkCoverableForDoubleInsurance',
        [jobId, linePrefix, coverableFixedId, accountNumber, sessionId],
        headers
    );
}

export async function setQuoteApprovedTimestamp(
    jobId: string,
    sessionId: string,
    headers: AxiosRequestHeaders = {}
): Promise<void> {
    return await RpcClientFactory.getInstance().rpcCall<void>(
        capability.PC_ONLINESALES,
        'setQuoteApprovedTimestamp',
        [jobId, sessionId],
        headers
    );
}

export async function getQuoteApprovedTimestamp(
    jobId: string,
    sessionId: string,
    headers: AxiosRequestHeaders = {}
): Promise<DtoTypes.PC.edge.time.LocalDateDTO> {
    return await RpcClientFactory.getInstance().rpcCall<DtoTypes.PC.edge.time.LocalDateDTO>(
        capability.PC_ONLINESALES,
        'getQuoteApprovedTimestamp',
        [jobId, sessionId],
        headers
    );
}

export async function removeApprovedTimestampAndReminder(
    jobId: string,
    sessionId: string,
    headers: AxiosRequestHeaders = {}
): Promise<void> {
    return await RpcClientFactory.getInstance().rpcCall<void>(
        capability.PC_ONLINESALES,
        'removeApprovedTimestampAndReminder',
        [jobId, sessionId],
        headers
    );
}

export async function updateHasInsuranceDebts(
    jobId: string,
    sessionId: string,
    hasInsuranceDebts: DtoTypes.PC.top.edge.capabilities.onlinesales.dto.common.HasInsuranceDebtsDTO[],
    headers: AxiosRequestHeaders = {}
): Promise<void> {
    return await RpcClientFactory.getInstance().rpcCall<void>(
        capability.PC_ONLINESALES,
        'updateHasInsuranceDebts',
        [jobId, sessionId, hasInsuranceDebts],
        headers
    );
}

export async function getQuoteDocumentInfo(
    jobId: string,
    sessionId: string,
    headers: AxiosRequestHeaders = {}
): Promise<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.quote.QuoteDocumentInfoDTO> {
    return await RpcClientFactory.getInstance().rpcCall<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.quote.QuoteDocumentInfoDTO>(
        capability.PC_ONLINESALES,
        'getQuoteDocumentInfo',
        [jobId, sessionId],
        headers
    );
}

export async function getJobStatus(
    jobId: string,
    sessionId: string,
    headers: AxiosRequestHeaders = {}
): Promise<DtoTypes.PC.typekey.PolicyPeriodStatus> {
    return await RpcClientFactory.getInstance().rpcCall<DtoTypes.PC.typekey.PolicyPeriodStatus>(
        capability.PC_ONLINESALES,
        'getJobStatus',
        [jobId, sessionId],
        headers
    );
}

export async function calculateEditPriceForOPC(
    jobId: string,
    sessionId: string,
    editCoverable: DtoTypes.PC.top.edge.capabilities.onlinesales.dto.opc.EditCoverableAttributesDTO,
    headers: AxiosRequestHeaders = {}
): Promise<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.opc.EditCoverablePriceDTO> {
    return await RpcClientFactory.getInstance().rpcCall<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.opc.EditCoverablePriceDTO>(
        capability.PC_ONLINESALES,
        'calculateEditPriceForOPC',
        [jobId, sessionId, editCoverable],
        headers
    );
}

export async function getCoveragesForOPC(
    policyNumber: string,
    coverableChangeDTO: DtoTypes.PC.top.edge.capabilities.onlinesales.dto.opc.CoverableChangeDTO,
    headers: AxiosRequestHeaders = {}
): Promise<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.opc.CoverageOpcDTO[]> {
    return await RpcClientFactory.getInstance().rpcCall<
        DtoTypes.PC.top.edge.capabilities.onlinesales.dto.opc.CoverageOpcDTO[]
    >(capability.PC_ONLINESALES, 'getCoveragesForOPC', [policyNumber, coverableChangeDTO], headers);
}

export async function getTermOPCInfo(
    jobId: string,
    sessionId: string,
    editTermDTO: DtoTypes.PC.top.edge.capabilities.onlinesales.dto.opc.EditTermDTO,
    headers: AxiosRequestHeaders = {}
): Promise<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.opc.OPCTermDTO> {
    return await RpcClientFactory.getInstance().rpcCall<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.opc.OPCTermDTO>(
        capability.PC_ONLINESALES,
        'getTermOPCInfo',
        [jobId, sessionId, editTermDTO],
        headers
    );
}

export async function calculatePriceForEditedCoverageOPC(
    jobId: string,
    sessionId: string,
    editTermDTO: DtoTypes.PC.top.edge.capabilities.onlinesales.dto.opc.EditTermDTO,
    headers: AxiosRequestHeaders = {}
): Promise<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.opc.EditTermPriceDTO> {
    return await RpcClientFactory.getInstance().rpcCall<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.opc.EditTermPriceDTO>(
        capability.PC_ONLINESALES,
        'calculatePriceForEditedCoverageOPC',
        [jobId, sessionId, editTermDTO],
        headers
    );
}

export async function moveSubmissionToCustomerAccount(
    targetJobNumber: string,
    sourceJobNumber: string,
    targetSessionId: string,
    sourceSessionID: string,
    headers: AxiosRequestHeaders = {}
): Promise<ConflictingCoverableDTO[]> {
    return await RpcClientFactory.getInstance().rpcCall<ConflictingCoverableDTO[]>(
        capability.PC_ONLINESALES,
        'moveSubmissionToCustomerAccount',
        [targetJobNumber, sourceJobNumber, targetSessionId, sourceSessionID],
        headers
    );
}

export async function createPolicyChangeForOPC(
    policyNumber: string,
    coverableId: string,
    productName: DtoTypes.PC.typekey.ProductName_Top,
    headers: AxiosRequestHeaders = {}
): Promise<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.JobInfoDTO> {
    return await RpcClientFactory.getInstance().rpcCall<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.JobInfoDTO>(
        capability.PC_ONLINESALES,
        'createPolicyChangeForOPC',
        [policyNumber, coverableId, productName],
        headers
    );
}

export async function createPolicyChangeWithRenewalDateForOPC(
    policyChangeId: string,
    sessionId: string,
    headers: AxiosRequestHeaders = {}
): Promise<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.JobInfoDTO> {
    return await RpcClientFactory.getInstance().rpcCall<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.JobInfoDTO>(
        capability.PC_ONLINESALES,
        'createPolicyChangeWithRenewalDateForOPC',
        [policyChangeId, sessionId],
        headers
    );
}

export async function getCarReplacements(
    jobId: string,
    sessionId: string,
    fixedId: string,
    headers: AxiosRequestHeaders = {}
): Promise<ReplacementCarDTO[]> {
    return await RpcClientFactory.getInstance().rpcCall<ReplacementCarDTO[]>(
        capability.PC_ONLINESALES,
        'getCarReplacements',
        [jobId, sessionId, fixedId],
        headers
    );
}

export async function setCarReplacement(
    jobId: string,
    sessionId: string,
    sourceCarfixedID: string,
    targetCarfixedID: string,
    headers: AxiosRequestHeaders = {}
): Promise<void> {
    return await RpcClientFactory.getInstance().rpcCall<void>(
        capability.PC_ONLINESALES,
        'setCarReplacement',
        [jobId, sessionId, sourceCarfixedID, targetCarfixedID],
        headers
    );
}

export async function withdrawOPCShoppingBasket(
    jobId: string,
    policyNumber: string,
    sessionId: string,
    headers: AxiosRequestHeaders = {}
): Promise<void> {
    return await RpcClientFactory.getInstance().rpcCall<void>(
        capability.PC_ONLINESALES,
        'withdrawOPCShoppingBasket',
        [jobId, policyNumber, sessionId],
        headers
    );
}

export async function getAccountHolderData(
    jobNumber: string,
    sessionId: string,
    headers: AxiosRequestHeaders = {}
): Promise<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.account.AccountHolderDTO> {
    return await RpcClientFactory.getInstance().rpcCall<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.account.AccountHolderDTO>(
        capability.PC_ONLINESALES,
        'getAccountHolderData',
        [jobNumber, sessionId],
        headers
    );
}

export async function getCarYearlyMileagesWithSuggestion(
    jobId: string,
    sessionId: string,
    coverableId: string,
    odometerReading?: string,
    headers: AxiosRequestHeaders = {}
): Promise<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.opc.YearlyMileagesDTO> {
    return await RpcClientFactory.getInstance().rpcCall<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.opc.YearlyMileagesDTO>(
        capability.PC_ONLINESALES,
        'getCarYearlyMileagesWithSuggestion',
        [jobId, sessionId, coverableId, odometerReading],
        headers
    );
}

export async function validateCarOdometerReading(
    jobId: string,
    sessionId: string,
    coverableId: string,
    odometerReading: string,
    yearlyMileage: DtoTypes.PC.typekey.MVPYearlyMileage_Top,
    headers: AxiosRequestHeaders = {}
): Promise<void> {
    return await RpcClientFactory.getInstance().rpcCall<void>(
        capability.PC_ONLINESALES,
        'validateCarOdometerReading',
        [jobId, sessionId, coverableId, odometerReading, yearlyMileage],
        headers
    );
}

export async function generateQuoteDocument(
    jobId: string,
    sessionId: string,
    headers: AxiosRequestHeaders = {}
): Promise<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.common.GeneratedDocumentInfoDTO> {
    return await RpcClientFactory.getInstance().rpcCall<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.common.GeneratedDocumentInfoDTO>(
        capability.PC_ONLINESALES,
        'generateQuoteDocument',
        [jobId, sessionId],
        headers
    );
}

export async function withdrawJob(jobId: string, sessionId: string, headers: AxiosRequestHeaders = {}): Promise<void> {
    return await RpcClientFactory.getInstance().rpcCall<void>(
        capability.PC_ONLINESALES,
        'withdrawJob',
        [jobId, sessionId],
        headers
    );
}
